import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Share2, Copy, Mail, MessageSquare, X, Facebook, Twitter, MessageCircle } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Generic share button for events, player profiles, and forum threads.
 *
 * Props:
 *  - shareApiPath:  backend OG route, e.g. "/api/events/123/share"
 *  - title:         plain text title used for native share / social text
 *  - subtitle:      optional extra line (e.g. date+venue, "Instructor", category)
 *  - label:         button label text, default "Share"
 *  - testId:        override the data-testid (default "share-btn")
 *
 * Uses `navigator.share` on supported browsers/mobile, otherwise opens a modal
 * with Copy + Facebook + X + WhatsApp + SMS + Email targets.
 */
export default function ShareButton({ shareApiPath, title, subtitle = '', label = 'Share', testId = 'share-btn' }) {
  const [open, setOpen] = useState(false);
  if (!shareApiPath || !title) return null;

  const shareUrl = `${BACKEND}${shareApiPath}`;
  const shareText = subtitle ? `${title} — ${subtitle}` : title;

  const handleClick = async () => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title, text: shareText, url: shareUrl });
        return;
      } catch (err) {
        if (err?.name === 'AbortError') return;
      }
    }
    setOpen(true);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard');
      setOpen(false);
    } catch {
      toast.error('Could not copy — please copy the link manually');
    }
  };

  const encoded = encodeURIComponent(shareUrl);
  const encodedText = encodeURIComponent(shareText);
  const targets = [
    { key: 'facebook', label: 'Facebook', icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${encoded}` },
    { key: 'twitter', label: 'X / Twitter', icon: Twitter, href: `https://twitter.com/intent/tweet?url=${encoded}&text=${encodedText}` },
    { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, href: `https://wa.me/?text=${encodedText}%20${encoded}` },
    { key: 'sms', label: 'Messages', icon: MessageSquare, href: `sms:?&body=${encodedText}%20${encoded}` },
    { key: 'email', label: 'Email', icon: Mail, href: `mailto:?subject=${encodeURIComponent(title)}&body=${encodedText}%0A%0A${encoded}` },
  ];

  return (
    <>
      <Button onClick={handleClick} variant="outline" className="rounded-full gap-2" data-testid={testId}>
        <Share2 className="w-4 h-4" /> {label}
      </Button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4"
          data-testid={`${testId}-modal`}
          onClick={() => setOpen(false)}
        >
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Share this
              </h3>
              <button
                onClick={() => setOpen(false)}
                className="p-1 hover:bg-slate-100 rounded-full"
                aria-label="Close"
                data-testid={`${testId}-modal-close`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs text-slate-500 mb-4">
              The shared link shows a rich preview with a photo, title, and details.
            </p>

            <div className="grid grid-cols-3 gap-3 mb-4">
              {targets.map((t) => {
                const Icon = t.icon;
                return (
                  <a
                    key={t.key}
                    href={t.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setOpen(false)}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl border border-slate-200 hover:border-[#56c6df] hover:bg-[#56c6df]/5 transition-all"
                    data-testid={`${testId}-target-${t.key}`}
                  >
                    <Icon className="w-5 h-5 text-slate-600" />
                    <span className="text-xs font-medium text-slate-700">{t.label}</span>
                  </a>
                );
              })}
            </div>

            <div className="flex items-center gap-2 p-2 rounded-xl bg-slate-50 border border-slate-200">
              <input
                type="text"
                readOnly
                value={shareUrl}
                className="flex-1 bg-transparent text-xs text-slate-600 px-2 outline-none"
                data-testid={`${testId}-link-input`}
                onFocus={(e) => e.target.select()}
              />
              <Button
                onClick={copyLink}
                size="sm"
                className="rounded-lg bg-[#56c6df] text-white hover:bg-[#3db0c9] gap-1"
                data-testid={`${testId}-copy`}
              >
                <Copy className="w-3.5 h-3.5" /> Copy
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
